import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BiArrowBack } from 'react-icons/bi'
import { UserContext } from 'stores/UserStore'
import { JobDocumentsContext } from 'stores/JobDocumentsStore'
import { FlexView, Card, LoadingOverlay, Button } from '../../components/common'
import { NewJobModal } from './components/newJobModal'
import { DeleteJobModal } from './components/deleteJobModal'
import * as S from "./styles"
import moment from 'moment'
import FilterableTableUpload from 'app-components/common/FilterableTableUpload'
import styled from 'styled-components'

const BoxlessTable = styled(FilterableTableUpload)`
  box-shadow: none;
  margin: 0px;
  width: 100%; 

  table {
    width: 100%;
    height: 100%;
    white-space: nowrap;
    th, td {
      text-align: left;
      border-right: 0;
    }
}
`
const JobUpload = () => {
  const [showNewJobModal, setShowNewJobModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedJob, setSelectedJob] = useState({})
  const [data, setData] = useState([])
  const [customers, setCustomers] = useState([])

  const { tableData, isLoading, getJobs } = useContext(JobDocumentsContext)
  const { currentUser, settings, registryLog } = useContext(UserContext)

  const { t } = useTranslation()

  // table columns
  const columns = [
    {
      Header: t('upload.date'),
      id: 'jobDate',
      accessor: row => moment(row.jobDate).format('DD/MM/YYYY'),
      filterable: true,
    },
    {
      Header: t('upload.rigName'),
      accessor: 'rig',
      id: 'rig',
      canFilter: true
    },
    {
      Header: t('upload.country'),
      accessor: 'country',
      id: 'country',
      canFilter: true
    },
    {
      Header: t('upload.type'),
      id: 'type',
      accessor: 'type',
      canFilter: true
    },
    {
      Header: t('upload.well'),
      id: 'well',
      accessor: 'well',
      canFilter: true
    },
    {
      Header: t('upload.area'),
      id: 'area',
      accessor: 'area',
      canFilter: true
    },
    {
      Header: t('upload.company'),
      id: 'customer',
      accessor: 'customer',
      canFilter: true
    }
  ]

  if (currentUser.roles.UPLOAD_JOBS.DELETE) {
    columns.push(
      {
        Header: t('upload.delete'),
        id: 'delete',
        Cell: ({ cell: { row } }) => <S.Trash size="1rem" style={{ alignSelf: 'center' }} onClick={() => {
          const { jobid, rig, jobDate } = row.original
          openDeleteJobModal({ id: jobid, rig: rig, jobDate: jobDate })
        }
        } />
      }
    )
  }

  const openDeleteJobModal = ({ id, rig, jobDate }) => {
    let date = new Date(jobDate)
    let newDateSplit = date.toISOString().split('T')[0]
    let newDateMoment = moment(newDateSplit)
    let newDateFormat = newDateMoment.format('DD/MM/YYYY')
    setSelectedJob({
      id,
      rig,
      jobDate: newDateFormat
    })
    setShowDeleteModal(true)
  }

  const closeDeleteJobModal = () => {
    setSelectedJob({})
    setShowDeleteModal(false)
  }

  const closeAndUpdateDeleteJobModal = async ({ success = true }) => {
    setSelectedJob({})
    setShowDeleteModal(false)
    if (success) {
      await getJobs()
    }
  }

  const openNewJobModal = () => {
    registryLog('click_openModalUpload', "click", "uploadNewJob")
    setShowNewJobModal(true)
  }

  const closeNewJobModal = () => {
    setShowNewJobModal(false)
  }

  const closeAndUpdateNewJobModal = async ({ success = true }) => {
    setShowNewJobModal(false)
    if (success) {
      await getJobs()
    }
  }

  const getSubsidiariesAndCustomersIds = useCallback(() => {
    const customers = currentUser.profiles.flatMap((prof) =>
      prof.roles.flatMap((roles) =>
        roles.settings.flatMap((setting) =>
          Object.assign({}, setting, { settings: setting.settings })
        )
      )
    )

    return customers.reduce((acc, curr) => {
      acc.customersIds.push(curr.id)
      const subsidiary = curr.settings
      subsidiary.forEach(item => {
        acc.subsidiariesIds.push(item.id)
      })
      return acc
    }, {
      customersIds: [],
      subsidiariesIds: []
    })
  }, [currentUser.profiles])

  const getCustomersOptions = useCallback(() => {
    const { customersIds, subsidiariesIds } = getSubsidiariesAndCustomersIds()
    const allCustomers = settings[0].settings

    const allSubsidiaries = allCustomers.filter((customer) => customersIds.includes(customer.name)).map(c => c.settings)

    const filteredSubsidiaries = allSubsidiaries.reduce((acc, curr) => {
      curr.forEach(company => {
        if (subsidiariesIds.includes(company.id)) {
          acc.push(company)
        }
      })
      return acc
    }, [])

    setCustomers(filteredSubsidiaries)

    return filteredSubsidiaries
  }, [getSubsidiariesAndCustomersIds, settings])

  const prepareTableData = useCallback(() => {
    const customers = getCustomersOptions()

    const treatedData = tableData.map(item => {
      const customer = customers.find(c => c.id === item.customer)

      return {
        ...item,
        customer: customer ? customer.description : item.costumer
      }
    })

    setData(treatedData)
  }, [getCustomersOptions, tableData])

  useEffect(() => {
    prepareTableData()
  }, [prepareTableData, tableData])

  return (
    <FlexView minWidth="100%" minHeight="92.5vh" alignItems="center" justifyContent="flex-start" fontSize="title" fontWeight="bold" backgroundColor="#E5E5E5">
      <LoadingOverlay visible={isLoading} fixed={true} />

      <S.BackButton href="/">
        <BiArrowBack size="2rem" />
      </S.BackButton>

      <Card width="85%" alignSelf="center" alignItems="center" justifyContent="space-between"  margin="30px 8px">
        {/* <Table width="100%"
            columns={columns}
            data={data}
            sortable
            elevation="none"
            borderRadius="card"
            paginated
            style={{ whiteSpace: "nowrap", backgroundColor: "white" }}
          /> */}
        <FlexView alignSelf="stretch">
          <Button alignSelf="flex-end" backgroundColor="primary" color="white" fontSize="big" onClick={() => openNewJobModal()}>{t('upload.upload')}</Button>
          <BoxlessTable columns={columns} data={data} sortable="true" paginated="true" />
        </FlexView>
      </Card>
      <NewJobModal
        show={showNewJobModal}
        close={closeNewJobModal}
        closeAndUpdate={closeAndUpdateNewJobModal}
        customers={customers}
      />

      <DeleteJobModal
        show={showDeleteModal}
        data={selectedJob}
        close={closeDeleteJobModal}
        closeAndUpdate={closeAndUpdateDeleteJobModal}
      />
    </FlexView >
  )
}

export default JobUpload